import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { isSafePath } from "@grandvisionhq/frk-routing";
import { useIntl } from "@grandvisionhq/state";
import { useDispatchTrackEvent, useTrackPageView } from "@grandvisionhq/svc-tealium/events";
import { cancelAppointmentScreenEvent } from "../../../../../../../../../data-layer/v3/data-layer/actions.js";
import { fromArrayToString } from "../../../../../../../../../utils/v3/utils/from-array-to-string.js";
import { useRoutingContext } from "../../../../../../../context/RoutingContext/v3/pages/CancelAppointment/context/RoutingContext/context.js";
import CancelAppointment from "./CancelAppointment.js";
import { useCancelAppointmentContext } from "../../../../../../../context/v3/pages/CancelAppointment/context/context.js";
import useAppointmentById from "../../../../../../../hooks/api/v3/pages/CancelAppointment/hooks/api/use-appointment-by-id.js";
import useAppointmentByToken from "../../../../../../../hooks/api/v3/pages/CancelAppointment/hooks/api/use-appointment-by-token.js";
import useCancelAppointmentByToken from "../../../../../../../hooks/api/v3/pages/CancelAppointment/hooks/api/use-cancel-appointment-by-token.js";
import useCancelAppointmentById from "../../../../../../../hooks/api/v3/pages/CancelAppointment/hooks/api/use-cancel-appointment-by-id.js";
import useVirtualAppointment from "../../../../../../../../BookAppointment/hooks/v3/pages/BookAppointment/hooks/use-virtual-appointment.js";
import useStoreById from "../../../../../../../hooks/api/v3/pages/CancelAppointment/hooks/api/use-store-by-id.js";
const INTERNAL_SERVER_ERROR_STATUS = "INTERNAL_SERVER_ERROR";
const CancelAppointmentContainer = ({ userToken, appointmentId, token }) => {
  const { appointment, setAppointment, setStore } = useCancelAppointmentContext();
  const [{ goTo }, { queryParams }] = useRoutingContext();
  const [errorCode, setErrorCode] = React.useState("");
  const { getLabel, getFormattedLabel } = useIntl();
  const labels = React.useMemo(
    () => ({
      title: getFormattedLabel("appointments.page.cancel.title", {
        labelData: { name: appointment?.invitee?.firstName || "" }
      }),
      subtitle: getLabel("appointments.page.cancel.subtitle"),
      reason: getLabel("appointments.page.cancel.reason"),
      cancelButton: getLabel("appointments.page.cancel.buttonLabel.cancel"),
      closeButton: getLabel("appointments.page.cancel.buttonLabel.close"),
      textareaLabel: getLabel("appointments.page.cancel.textareaLabel"),
      errorValidation: getLabel("appointments.page.cancel.errorValidation"),
      errorTitle: getLabel("appointments.page.cancel.errorTitle"),
      errorMessage: getLabel(`appointments.page.cancel.error.${errorCode.toLowerCase()}`, {
        hideLabelKey: true
      }),
      slowLoadingIndication: getLabel("appointments.page.cancel.slowLoadingIndication")
    }),
    [appointment, errorCode, getLabel, getFormattedLabel]
  );
  const [
    getAppointmentById,
    { data: appointmentById, loading: appointmentByIdLoading, error: appointmentByIdError }
  ] = useAppointmentById(
    {
      id: appointmentId ?? ""
    },
    userToken ?? ""
  );
  const [
    getAppointmentByToken,
    {
      data: appointmentByToken,
      loading: appointmentByTokenLoading,
      error: appointmentByTokenError
    }
  ] = useAppointmentByToken({
    token: token ?? ""
  });
  const [
    cancelAppointmentByToken,
    {
      data: cancelAppointmentByTokenData,
      loading: cancelAppointmentByTokenDataLoading,
      error: cancelAppointmentByTokenError
    }
  ] = useCancelAppointmentByToken();
  const [
    cancelAppointmentById,
    {
      data: cancelAppointmentByIdData,
      loading: cancelAppointmentByIdDataLoading,
      error: cancelAppointmentByIdError
    }
  ] = useCancelAppointmentById(userToken ?? "");
  const [{ isVirtualAppointmentType }] = useVirtualAppointment();
  const [getStoreById, { data: store, loading: storeLoading }] = useStoreById({
    globalStoreId: "0"
  });
  const aabCancellationEvent = React.useCallback(
    () => Promise.resolve({
      id: "Event",
      Events_FunnelCancellation: "1"
    }),
    []
  );
  const dispatchTrackEvent = useDispatchTrackEvent();
  const onSubmit = React.useCallback(
    (cancellationReason) => {
      setErrorCode("");
      if (token) {
        cancelAppointmentByToken({
          variables: { token, ...cancellationReason ? { cancellationReason } : {} }
        });
      }
      if (appointmentId) {
        cancelAppointmentById({
          variables: { id: appointmentId, ...cancellationReason ? { cancellationReason } : {} }
        });
      }
      dispatchTrackEvent(aabCancellationEvent);
    },
    [token, appointmentId, cancelAppointmentByToken, cancelAppointmentById]
  );
  React.useEffect(() => {
    if (appointment && store) {
      const dataLayerEvent = cancelAppointmentScreenEvent({
        appointment: {
          startTime: appointment.startTime,
          endTime: appointment.endTime,
          appointmentType: {
            id: appointment.appointmentType?.id || "",
            ageGroup: appointment.appointmentType?.ageGroup ?? "ADULT"
          }
        },
        store
        // TODO: MACC-19077
      });
      DataLayer.pushEvent(dataLayerEvent);
    }
  }, [appointment, store]);
  React.useEffect(() => {
    if (!cancelAppointmentByTokenData) {
      return;
    }
    goTo("confirmation", queryParams);
  }, [cancelAppointmentByTokenData, queryParams, goTo]);
  React.useEffect(() => {
    if (!cancelAppointmentByIdData) {
      return;
    }
    goTo("confirmation", queryParams);
  }, [cancelAppointmentByIdData, queryParams, goTo]);
  React.useEffect(() => {
    if (appointmentId && userToken) {
      getAppointmentById({ variables: { id: appointmentId } });
    }
  }, [appointmentId, userToken]);
  React.useEffect(() => {
    if (token) {
      getAppointmentByToken({ variables: { token } });
    }
  }, [token]);
  React.useEffect(() => {
    if (!appointmentById) {
      return;
    }
    setAppointment(appointmentById);
  }, [appointmentById]);
  React.useEffect(() => {
    if (!appointmentByToken) {
      return;
    }
    setAppointment(appointmentByToken);
  }, [appointmentByToken]);
  React.useEffect(() => {
    if (appointmentByIdError) {
      throw new Error(
        appointmentByIdError.graphQLErrors?.[0]?.extensions?.code ?? INTERNAL_SERVER_ERROR_STATUS
      );
    }
    if (appointmentByTokenError) {
      throw new Error(
        appointmentByTokenError?.graphQLErrors?.[0]?.extensions?.code ?? INTERNAL_SERVER_ERROR_STATUS
      );
    }
  }, [appointmentByIdError, appointmentByTokenError]);
  React.useEffect(() => {
    if (!store) {
      return;
    }
    setStore(store);
  }, [store]);
  React.useEffect(() => {
    const isVirtualAppointment = isVirtualAppointmentType(appointment?.appointmentType);
    const globalStoreId = isVirtualAppointment ? void 0 : appointment?.storeInfo?.globalStoreId;
    if (globalStoreId) {
      getStoreById({
        variables: {
          globalStoreId
        }
      });
    }
  }, [appointment]);
  React.useEffect(() => {
    if (cancelAppointmentByTokenError) {
      setErrorCode(
        cancelAppointmentByTokenError?.graphQLErrors?.[0]?.extensions?.code ?? INTERNAL_SERVER_ERROR_STATUS
      );
    }
    if (cancelAppointmentByIdError) {
      setErrorCode(
        cancelAppointmentByIdError?.graphQLErrors?.[0]?.extensions?.code ?? INTERNAL_SERVER_ERROR_STATUS
      );
    }
  }, [cancelAppointmentByIdError, cancelAppointmentByTokenError]);
  const aabCancellationPageView = React.useCallback(() => {
    return new Promise((resolve) => {
      if (!appointment || !store) {
        return;
      }
      resolve({
        id: "VirtualPage-View",
        Page_Type: "Cancellation",
        Page_Section1: "Exam"
      });
    });
  }, [appointment, store]);
  useTrackPageView(aabCancellationPageView);
  const rawReturnPath = fromArrayToString(queryParams.returnPath) ?? "/";
  const safeReturnPath = isSafePath(rawReturnPath) ? rawReturnPath : "/";
  return /* @__PURE__ */ React.createElement(
    CancelAppointment,
    {
      appointment: appointment ?? void 0,
      store: store ?? void 0,
      isAppointmentLoading: appointmentByIdLoading || appointmentByTokenLoading,
      isStoreLoading: storeLoading,
      labels,
      returnPath: safeReturnPath,
      onSubmit,
      isCancellationInProgress: cancelAppointmentByTokenDataLoading || cancelAppointmentByIdDataLoading,
      hasError: !!errorCode,
      disableCancelAppointmentButton: !!errorCode && errorCode !== INTERNAL_SERVER_ERROR_STATUS
    }
  );
};
export {
  CancelAppointmentContainer as default
};
//# sourceMappingURL=CancelAppointmentContainer.js.map
